import React, { useState, useEffect } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    Typography,
    Box,
    LinearProgress,
    Chip,
    IconButton,
    Divider,
    Paper
} from "@material-ui/core";
import { DeleteOutline, AddCircleOutline } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import {
    createWhatsAppStatus,
    getWhatsAppStatus,
    uploadStatusMedia,
    getContacts
} from "../../services/whatsappStatus";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    paper: {
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    colorPicker: {
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    colorBox: {
        width: "100%",
        height: 50,
        borderRadius: 4,
        cursor: "pointer",
        border: "2px solid transparent",
        transition: "all 0.2s",
        "&:hover": {
            transform: "scale(1.05)",
        },
    },
    colorBoxSelected: {
        border: `2px solid ${theme.palette.primary.main}`,
        transform: "scale(1.1)",
    },
    mediaPreview: {
        maxWidth: "100%",
        maxHeight: 200,
        marginTop: theme.spacing(2),
        borderRadius: 4,
    },
    itemBlock: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: "rgba(0,0,0,0.02)",
        border: "1px solid rgba(0,0,0,0.1)",
        position: "relative"
    },
    removeButton: {
        position: "absolute",
        top: theme.spacing(1),
        right: theme.spacing(1),
        color: theme.palette.error.main
    }
}));

const defaultItem = {
    type: "text",
    text: "",
    backgroundColor: 19,
    font: 0,
    mediaUrl: "",
    thumbnailUrl: "",
    mimetype: "",
    mentions: "",
    scheduledAt: ""
};

const WhatsAppStatusModal = ({ open, onClose, statusId }) => {
    const classes = useStyles();
    const [whatsapps, setWhatsapps] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    
    // Controling uploads by index using a simple state dict
    const [uploadingState, setUploadingState] = useState({});
    const [progressState, setProgressState] = useState({});

    const initialState = {
        whatsappIds: ["all"],
        items: [{ ...defaultItem }]
    };

    const [statusData, setStatusData] = useState(initialState);

    useEffect(() => {
        const fetchWhatsapps = async () => {
            try {
                const { data } = await api.get("/whatsapp");
                setWhatsapps(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchWhatsapps();
    }, []);

    useEffect(() => {
        const fetchContactsList = async () => {
            try {
                const data = await getContacts();
                setContacts(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchContactsList();
    }, []);

    useEffect(() => {
        const fetchStatus = async () => {
            if (!statusId) return;
            try {
                const data = await getWhatsAppStatus(statusId);
                // When editing, format scheduledAt to datetime-local expected format (YYYY-MM-DDTHH:mm)
                let dateStr = "";
                if (data.scheduledAt) {
                    const date = new Date(data.scheduledAt);
                    dateStr = date.toISOString().slice(0, 16);
                }
                
                setStatusData({
                    whatsappIds: data.whatsappId ? [data.whatsappId] : ["all"],
                    items: [{
                        type: data.type || "text",
                        text: data.text || "",
                        backgroundColor: data.backgroundColor || 19,
                        font: data.font || 0,
                        mediaUrl: data.mediaUrl || "",
                        thumbnailUrl: data.thumbnailUrl || "",
                        mimetype: data.mimetype || "",
                        mentions: data.mentions || "",
                        scheduledAt: dateStr
                    }]
                });

                if (data.mentions) {
                    setSelectedContacts(data.mentions.split(","));
                }
            } catch (err) {
                toastError(err);
            }
        };
        fetchStatus();
    }, [statusId]);

    const handleClose = () => {
        setStatusData(initialState);
        setUploadingState({});
        setProgressState({});
        setSelectedContacts([]);
        onClose();
    };

    const handleFileUpload = async (event, index, setFieldValue) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadingState(prev => ({ ...prev, [index]: true }));
        setProgressState(prev => ({ ...prev, [index]: 0 }));

        try {
            const progressInterval = setInterval(() => {
                setProgressState(prev => {
                    const curr = prev[index] || 0;
                    if (curr >= 90) {
                        clearInterval(progressInterval);
                        return { ...prev, [index]: 90 };
                    }
                    return { ...prev, [index]: curr + 10 };
                });
            }, 100);

            const data = await uploadStatusMedia(file);

            clearInterval(progressInterval);
            setProgressState(prev => ({ ...prev, [index]: 100 }));

            setFieldValue(`items[${index}].mediaUrl`, data.url);
            setFieldValue(`items[${index}].mimetype`, file.type);

            toast.success("Arquivo enviado com sucesso");
        } catch (err) {
            toastError(err);
        } finally {
            setUploadingState(prev => ({ ...prev, [index]: false }));
            setTimeout(() => setProgressState(prev => ({ ...prev, [index]: 0 })), 1000);
        }
    };

    const handleSaveStatus = async (values) => {
        try {
            // Formatar os IDs para envio
            let wids = values.whatsappIds || [];
            if (wids.includes("all") || wids.length === 0) {
                wids = ["all"]; // O Backend interpreta "all" ou array vazio como "Todas as Conexões"
            }
            
            // Grava cada item da lista sequencialmente
            for (const item of values.items) {
                await createWhatsAppStatus({
                    whatsappIds: wids,
                    type: item.type,
                    text: item.text,
                    backgroundColor: item.backgroundColor,
                    font: item.font,
                    mediaUrl: item.mediaUrl,
                    thumbnailUrl: item.thumbnailUrl,
                    mimetype: item.mimetype,
                    mentions: item.mentions,
                    scheduledAt: item.scheduledAt ? new Date(item.scheduledAt).toISOString() : null
                });
            }
            
            toast.success(values.items.length > 1 ? "Status agendados com sucesso!" : "Status salvo com sucesso!");
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const backgroundColors = [
        { id: 1, color: "#FFD700", name: "Amarelo 1" },
        { id: 2, color: "#FFC700", name: "Amarelo 2" },
        { id: 3, color: "#FFB700", name: "Amarelo 3" },
        { id: 4, color: "#90EE90", name: "Verde 1" },
        { id: 5, color: "#7CFC00", name: "Verde 2" },
        { id: 6, color: "#32CD32", name: "Verde 3" },
        { id: 7, color: "#87CEEB", name: "Azul 1" },
        { id: 8, color: "#4682B4", name: "Azul 2" },
        { id: 9, color: "#1E90FF", name: "Azul 3" },
        { id: 10, color: "#DDA0DD", name: "Lilás 1" },
        { id: 11, color: "#BA55D3", name: "Lilás 2" },
        { id: 12, color: "#9370DB", name: "Lilás 3" },
        { id: 13, color: "#FF00FF", name: "Magenta" },
        { id: 14, color: "#FFB6C1", name: "Rosa 1" },
        { id: 15, color: "#FF69B4", name: "Rosa 2" },
        { id: 16, color: "#D2B48C", name: "Marrom claro" },
        { id: 17, color: "#D3D3D3", name: "Cinza 1" },
        { id: 18, color: "#A9A9A9", name: "Cinza 2" },
        { id: 19, color: "#808080", name: "Cinza 3 (Padrão)" },
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                className: classes.paper
            }}
        >
            <DialogTitle>
                {statusId ? "Editar Status" : "Novo(s) Status"}
            </DialogTitle>
            <Formik
                initialValues={statusData}
                enableReinitialize={true}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSaveStatus(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                        <DialogContent dividers>
                            <Box mb={3}>
                                <FormControl
                                    variant="outlined"
                                    fullWidth
                                >
                                    <InputLabel>Conexão (WhatsApp)</InputLabel>
                                    <Select
                                        multiple
                                        value={values.whatsappIds || []}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            if (selected.includes("all") && !(values.whatsappIds || []).includes("all")) {
                                                // Se "Todas" acabou de ser selecionado
                                                setFieldValue("whatsappIds", ["all"]);
                                            } else if (selected.includes("all") && selected.length > 1) {
                                                // Se já estava com "Todas" e selecionou outra, desmarca "Todas"
                                                setFieldValue("whatsappIds", selected.filter(v => v !== "all"));
                                            } else {
                                                setFieldValue("whatsappIds", selected);
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) return "Selecione a(s) conexão(ões)";
                                            if (selected.includes("all")) return "Todas as minhas conexões";
                                            return selected.map(id => whatsapps.find(w => w.id === id)?.name || id).join(", ");
                                        }}
                                    >
                                        <MenuItem value="all">
                                            <strong>Todas as minhas conexões</strong>
                                        </MenuItem>
                                        {whatsapps.map(whatsapp => (
                                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                                {whatsapp.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            <Divider style={{marginBottom: 20}} />

                            <FieldArray name="items">
                                {({ insert, remove, push }) => (
                                    <div>
                                        {values.items.map((item, index) => (
                                            <Paper elevation={0} className={classes.itemBlock} key={index}>
                                                {values.items.length > 1 && (
                                                    <IconButton
                                                        className={classes.removeButton}
                                                        size="small"
                                                        onClick={() => remove(index)}
                                                    >
                                                        <DeleteOutline />
                                                    </IconButton>
                                                )}
                                                
                                                <Typography variant="subtitle1" gutterBottom>
                                                    <strong>Agendamento #{index + 1}</strong>
                                                </Typography>

                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6}>
                                                        <FormControl variant="outlined" fullWidth>
                                                            <InputLabel>Tipo</InputLabel>
                                                            <Field
                                                                as={Select}
                                                                label="Tipo"
                                                                name={`items[${index}].type`}
                                                            >
                                                                <MenuItem value="text">Texto</MenuItem>
                                                                <MenuItem value="image">Imagem</MenuItem>
                                                                <MenuItem value="video">Vídeo</MenuItem>
                                                                <MenuItem value="audio">Áudio</MenuItem>
                                                            </Field>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Field
                                                            as={TextField}
                                                            label="Agendar para (Opcional)"
                                                            type="datetime-local"
                                                            variant="outlined"
                                                            fullWidth
                                                            name={`items[${index}].scheduledAt`}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <FormControl variant="outlined" fullWidth>
                                                            <InputLabel>Mencionar Contatos (opcional)</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={item.mentions ? item.mentions.split(',') : []}
                                                                onChange={(e) => {
                                                                    const mentionsString = e.target.value.join(",");
                                                                    setFieldValue(`items[${index}].mentions`, mentionsString);
                                                                }}
                                                                label="Mencionar Contatos (opcional)"
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.map((value) => {
                                                                            const contact = contacts.find(c => c.number === value);
                                                                            return (
                                                                                <Chip
                                                                                    key={value}
                                                                                    label={contact?.name || value}
                                                                                    size="small"
                                                                                />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {contacts.map((contact) => (
                                                                    <MenuItem key={contact.id} value={contact.number}>
                                                                        {contact.name} ({contact.number})
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>

                                                    {item.type === "text" && (
                                                        <>
                                                            <Grid item xs={12}>
                                                                <Field
                                                                    as={TextField}
                                                                    label="Texto"
                                                                    name={`items[${index}].text`}
                                                                    multiline
                                                                    rows={4}
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    helperText={`${item.text?.length || 0}/656 caracteres`}
                                                                />
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Cor de Fundo
                                                                </Typography>
                                                                <div className={classes.colorPicker}>
                                                                    {backgroundColors.map(bg => (
                                                                        <Box
                                                                            key={bg.id}
                                                                            className={`${classes.colorBox} ${item.backgroundColor === bg.id
                                                                                ? classes.colorBoxSelected
                                                                                : ""
                                                                                }`}
                                                                            style={{ backgroundColor: bg.color }}
                                                                            onClick={() => setFieldValue(`items[${index}].backgroundColor`, bg.id)}
                                                                            title={bg.name}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <FormControl variant="outlined" fullWidth>
                                                                    <InputLabel>Fonte</InputLabel>
                                                                    <Field
                                                                        as={Select}
                                                                        label="Fonte"
                                                                        name={`items[${index}].font`}
                                                                    >
                                                                        <MenuItem value={0}>Padrão</MenuItem>
                                                                        <MenuItem value={1}>Estilo 1</MenuItem>
                                                                        <MenuItem value={2}>Estilo 2</MenuItem>
                                                                        <MenuItem value={3}>Estilo 3</MenuItem>
                                                                        <MenuItem value={4}>Estilo 4</MenuItem>
                                                                        <MenuItem value={5}>Estilo 5</MenuItem>
                                                                        <MenuItem value={6}>Estilo 6</MenuItem>
                                                                        <MenuItem value={7}>Estilo 7</MenuItem>
                                                                        <MenuItem value={8}>Estilo 8</MenuItem>
                                                                    </Field>
                                                                </FormControl>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    {["image", "video", "audio"].includes(item.type) && (
                                                        <>
                                                            <Grid item xs={12}>
                                                                <Button
                                                                    variant="contained"
                                                                    component="label"
                                                                    disabled={uploadingState[index]}
                                                                    fullWidth
                                                                >
                                                                    {uploadingState[index] ? "Enviando..." : "Selecionar Arquivo"}
                                                                    <input
                                                                        type="file"
                                                                        hidden
                                                                        accept={
                                                                            item.type === "image"
                                                                                ? "image/*"
                                                                                : item.type === "video"
                                                                                    ? "video/*"
                                                                                    : "audio/*"
                                                                        }
                                                                        onChange={(e) => handleFileUpload(e, index, setFieldValue)}
                                                                    />
                                                                </Button>
                                                                {progressState[index] > 0 && progressState[index] < 100 && (
                                                                    <Box mt={2}>
                                                                        <LinearProgress variant="determinate" value={progressState[index]} />
                                                                    </Box>
                                                                )}
                                                            </Grid>

                                                            {item.mediaUrl && (
                                                                <Grid item xs={12}>
                                                                    {item.type === "image" && (
                                                                        <img
                                                                            src={item.mediaUrl}
                                                                            alt="Preview"
                                                                            className={classes.mediaPreview}
                                                                        />
                                                                    )}
                                                                    {item.type === "video" && (
                                                                        <video
                                                                            src={item.mediaUrl}
                                                                            controls
                                                                            className={classes.mediaPreview}
                                                                        />
                                                                    )}
                                                                    {item.type === "audio" && (
                                                                        <audio src={item.mediaUrl} controls style={{ width: "100%" }} />
                                                                    )}
                                                                </Grid>
                                                            )}

                                                            <Grid item xs={12}>
                                                                <Field
                                                                    as={TextField}
                                                                    label="Legenda (opcional)"
                                                                    name={`items[${index}].text`}
                                                                    multiline
                                                                    rows={2}
                                                                    variant="outlined"
                                                                    fullWidth
                                                                />
                                                            </Grid>
                                                        </>
                                                    )}

                                                </Grid>
                                            </Paper>
                                        ))}
                                        
                                        {!statusId && (
                                          <Button
                                              type="button"
                                              variant="outlined"
                                              color="primary"
                                              startIcon={<AddCircleOutline />}
                                              onClick={() => {
                                                  const lastItem = values.items[values.items.length - 1];
                                                  // Adiciona copiando as mídias/textos do anterior
                                                  push({
                                                      ...lastItem,
                                                      scheduledAt: "" // Reseta a data para o usuário escolher
                                                  });
                                              }}
                                          >
                                              Adicionar outro agendamento
                                          </Button>
                                        )}
                                    </div>
                                )}
                            </FieldArray>

                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </Button>
                            <div className={classes.btnWrapper}>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting}
                                >
                                    Salvar
                                </Button>
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </div>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default WhatsAppStatusModal;
